import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import ProcessingStatus from '../processing_status/processing_status';
import { purchaseDetailsTypes, purchaseErrorText } from '../../../../common/config/const';
import ThreeDSecure from '../../../../common/threeDSecure';
import {
  getFollowupParam,  renderButtonTitle, selectCloseButtonActionIfNeed,
  isAffiliatePromoPopup, isSideMenu, sign, onRetryButtonClick, onConfirmButton,
  mpTrackPurchaseStartedEvent, mpTrackPurchaseEvent, mpTrackPurchaseFailedEvent
} from '../purchases_utils';
import { browserContext } from '../../../../common/config/utils';
import CreditCardPayment from '../../../../common/credit_card_payment/credit_card_payment';
import { log } from '../../../../common/config/app_logger';
import { useLocalizedNavigate } from '../../routes/hooks/useLocalizedNavigate';

const shouldRenderProcessingStatus = ({
  errorMessage, okMessage, userLoading, renderStatusPopup
}) => renderStatusPopup && (errorMessage || okMessage || userLoading);

const onPurchaseClickedHandler = ({
  e, disabled, startCreditCardProcessingPurchase, purchaseURL, onClickHandler
}) => {
  e.preventDefault();
  if (disabled) return;
  onClickHandler?.();
  startCreditCardProcessingPurchase(purchaseURL);
};

const closePurchaseDetailsOnSideMenu = (onPurchaseComplete, type) => {
  if (onPurchaseComplete && isSideMenu({ type })) onPurchaseComplete();
};

function CreditCardProcessing({
  purchaseURL,
  onPurchaseComplete,
  getUser,
  hasPaypalBillingAgreement = false,
  loadAdvisor,
  type = purchaseDetailsTypes.SIDE_MENU,
  children,
  purchaseAmount,
  t,
  bonus = 0,
  cancelChat,
  userLoading,
  currentPaymentOption,
  isContinue = false,
  disabled = false,
  hidePaymentDetails,
  advisorId,
  cancelChatOnPP,
  chatType,
  hideAffiliatePromoPopup,
  error,
  getCreditCardProcessingData,
  purchaseData,
  purchaseResponce = {},
  createCreditCardProcessingPurchase,
  getCreditCardProcessingPurchaseResponce,
  clearCreditCardProcessing,
  purchaseParams,
  dispatch,
  loadPaymentOptions,
  purchase3DSRequiredEvent,
  purchase3DSFailedEvent,
  fees = {},
  notShowPurchaseSuccessPoppup = false,
  renderStatusPopup = true,
  onClickHandler,
  onPurchaseFailed,
  topupFlow
}) {
  const location = useLocation();
  const history = useLocalizedNavigate();
  const [retryCount, setRetryCount] = useState(0);
  const [creditCardPaymentStatus, setCreditCardPaymentStatus] = useState({});
  const { purchaseFees } = fees;

  const setCreditCardPaymentStatusHandler = (value) => {
    setCreditCardPaymentStatus(pendingCreditCardPaymentStatus => ({
      ...pendingCreditCardPaymentStatus,
      ...value
    }));
  };

  const clearCreditCardPaymentStatus = () => {
    setCreditCardPaymentStatus({});
  };

  const startCreditCardProcessingPurchase = (URL) => {
    if (!URL) return;
    const params = {
      use_reference_transaction: true,
      with3ds: true,
      ...getFollowupParam({ isContinue, type, topupFlow }),
      ...browserContext
    };
    const url = `${ URL }${ sign({ URL }) }`;
    getCreditCardProcessingData({ url, params });
  };

  useEffect(() => () => {
    clearCreditCardProcessing();
  }, []);

  useEffect(() => {
    if (creditCardPaymentStatus.purchaseStarted) {
      mpTrackPurchaseStartedEvent({
        retryCount, conversionEventId: purchaseParams.conversionEventId, type, purchaseAmount,
        bonus, hasPaypalBillingAgreement, threeDAuthResult: purchaseResponce.threedAuthResult,
        currentPaymentOption, chatType, dispatch, purchaseFees, location, advisorId
      });
      setCreditCardPaymentStatusHandler({ purchaseStarted: false });
    }
    if (creditCardPaymentStatus.okMessage && !creditCardPaymentStatus.purchaseEventTracked) {
      const {
        conversionEventId, pathname, threedAuthResult, extraEventParams
      } = creditCardPaymentStatus.okMessage;
      mpTrackPurchaseEvent({
        retryCount, conversionEventId, type, purchaseAmount, bonus, hasPaypalBillingAgreement,
        threeDAuthResult: threedAuthResult, currentPaymentOption, chatType, dispatch,
        pathname, getUser, extraEventParams, location, advisorId
      });
      setCreditCardPaymentStatusHandler({ purchaseEventTracked: true });
    }
    if (creditCardPaymentStatus.errorMessage) {
      const {
        errorType, errorPathname, errorMessage, errorName, errorCode, gatewayName,
        threedAuthResult, extraEventParams, spendingLimits
      } = creditCardPaymentStatus.errorMessage;
      mpTrackPurchaseFailedEvent({
        errorType, errorMessage, errorName, errorCode, gatewayName, errorPathname,
        threeDAuthResult: threedAuthResult, retryCount, type, purchaseAmount, bonus,
        hasPaypalBillingAgreement, currentPaymentOption, chatType, dispatch, extraEventParams, spendingLimits, location, topupFlow, advisorId
      });
      if (onPurchaseFailed) {
        onPurchaseFailed(creditCardPaymentStatus.errorMessage);
      }
    }
  }, [creditCardPaymentStatus]);

  const renderCreditCardPayment = () => {
    const creditCardPaymentProps = {
      paymentsParams: purchaseParams,
      createCreditCardProcessingPurchase,
      getCreditCardProcessingPurchaseResponce,
      paymentsData: purchaseData,
      paymentsResponce: purchaseResponce,
      setCreditCardPaymentStatusHandler
    };
    return <CreditCardPayment { ...creditCardPaymentProps } />;
  };

  const purchase3DSRequiredEventHandler = (eventName, params) => {
    purchase3DSRequiredEvent(params);
  };

  const purchase3DSFailedEventHandler = (eventName, params) => {
    purchase3DSFailedEvent(params);
  };

  const renderThreeDSecure = () => {
    if (!creditCardPaymentStatus.showThreeDSecure) return null;
    const threeDSecureProps = {
      createCreditCardProcessingPurchase, paymentsParams: purchaseParams,
      purchase3DSRequiredEventAction: purchase3DSRequiredEventHandler,
      purchase3DSFailedEventAction: purchase3DSFailedEventHandler, log
    };
    return <ThreeDSecure { ...threeDSecureProps } />;
  };

  useEffect(() => {
    if (error && error.error === purchaseErrorText.paymentMethodCannotBeUsed) {
      loadPaymentOptions();
    }
  }, [error]);

  const onConfirmButtonClick = () => {
    onConfirmButton({
      setRetryCount, onPurchaseComplete
    });
  };

  const onCloseButtonClick = () => {
    clearCreditCardProcessing();
    clearCreditCardPaymentStatus();
    selectCloseButtonActionIfNeed({ type, cancelChatOnPP, hideAffiliatePromoPopup });
    closePurchaseDetailsOnSideMenu(onPurchaseComplete, type);
  };

  const onRetry = (e) => {
    e.preventDefault();
    setRetryCount(retryCount + 1);
    onRetryButtonClick({
      purchaseResponce, hidePaymentDetails, cancelChat, history, loadAdvisor, advisorId,
      purchaseURL, startPurchaseAction: startCreditCardProcessingPurchase,
      clearPurchaseAction: clearCreditCardProcessing,
      clearAdditionalAction: clearCreditCardPaymentStatus
    });
  };

  const renderProcessingStatus = () => {
    const { errorMessage, okMessage } = purchaseResponce;
    return (
      <ProcessingStatus
        purchaseResponce={ purchaseResponce }
        t={ t }
        isSideMenu={ isSideMenu({ type }) }
        isAffiliatePromoPopup={ isAffiliatePromoPopup({ type }) }
        onClose={ onCloseButtonClick }
        userLoading={ userLoading }
        onRetry={ onRetry }
        onConfirm={ onConfirmButtonClick }
        isCreditCard
        purchaseURL={ purchaseURL }
        buttonTitle={ renderButtonTitle({ isContinue, type, t }) }
        chatType={ chatType }
        purchaseAmount={ purchaseAmount }
        bonus={ bonus }
        error={ error }
        visible= { !!shouldRenderProcessingStatus({
          errorMessage, okMessage, userLoading, renderStatusPopup
        }) }
        notShowPurchaseSuccessPoppup={ notShowPurchaseSuccessPoppup }
      />
    );
  };

  const onPurchaseClicked = (e) => {
    onPurchaseClickedHandler({
      e, disabled, startCreditCardProcessingPurchase, purchaseURL, onClickHandler
    });
  };

  const onKeyDown = (e) => {
    e.preventDefault();
  };

  return (
    <>
      <a href={ purchaseURL } target="blank" onClick={ onPurchaseClicked } disabled={ disabled } onKeyDown={ onKeyDown } data-testid="credit-card-processing-a-container">
        { children }
      </a>
      { renderCreditCardPayment() }
      { renderThreeDSecure() }
      { renderProcessingStatus() }
    </>
  );
}

export default CreditCardProcessing;
